/**
 * UPSELL BUNDLES
 * 
 * Handles selection logic, quantity override, and USP visibility
 * for product upsell bundle options.
 */

class UpsellBundles extends HTMLElement {
  constructor() {
    super();
    this.productFormId = this.dataset.productFormId;
    this.sectionId = this.dataset.sectionId;
    this.productId = this.dataset.productId;
    this.selectedQuantity = null;
    this.selectedUpsellId = null;
    this.selectedPrice = null;
    this.selectedComparePrice = null;
    this.isSubmitting = false;
    this.isUpdatingButton = false;
    this.buttonObserver = null;
  }

  /**
   * Resolves the product form: explicit id first, then form inside
   * #ProductInformation-{sectionId} (theme block / product details).
   *
   * @returns {HTMLFormElement|null}
   */
  getProductForm() {
    if (this.productFormId) {
      const byId = document.getElementById(this.productFormId);
      if (byId && byId.tagName === 'FORM') {
        return /** @type {HTMLFormElement} */ (byId);
      }
    }
    const sid = this.sectionId;
    if (sid) {
      const root = document.getElementById('ProductInformation-' + sid);
      if (root) {
        const f = root.querySelector('form[data-type="add-to-cart-form"]');
        if (f) {
          return /** @type {HTMLFormElement} */ (f);
        }
      }
    }
    return null;
  }

  connectedCallback() {
    // Wait for DOM to be fully ready, especially for the button element
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.init();
      });
    } else {
      // Use a small delay to ensure buy-buttons script has initialized
      setTimeout(() => {
        this.init();
      }, 100);
    }
  }

  init() {
    // The Black Friday packs (assets/bf-packs.js) run the product form while Black Friday is on
    if (document.documentElement.classList.contains('bf-on') && this.closest('.shopify-section')?.querySelector('bf-packs')) {
      return;
    }

    const options = this.querySelectorAll('.upsell-bundles__option');
    const inputs = this.querySelectorAll('.upsell-bundles__input');

    // Set initial selected option
    const checkedInput = this.querySelector('.upsell-bundles__input:checked');
    if (checkedInput) {
      // Handle selection immediately
      this.handleSelection(checkedInput);
      
      // Set up observer and update button after a small delay to ensure methods are ready
      setTimeout(() => {
        // Also set up a MutationObserver to watch for button content changes
        // This catches when buy-buttons script updates the price
        if (this.setupButtonObserver) {
          this.setupButtonObserver();
        }
        
        // Update button price once
        if (this.updateButtonPrice) {
          this.updateButtonPrice();
        }
      }, 200);
      
      // One more retry after buy-buttons script has likely finished
      setTimeout(() => {
        if (this.updateButtonPrice && this.selectedPrice) {
          this.updateButtonPrice();
        }
      }, 800);
    } else {
      // If no option is pre-selected, update button to show default variant price
      setTimeout(() => {
        if (this.updateButtonToVariantPrice) {
          this.updateButtonToVariantPrice();
        }
      }, 150);
    }

    // Add click handlers
    options.forEach((option) => {
      option.addEventListener('click', (e) => {
        // Don't trigger if clicking on the input directly
        if (e.target.type === 'radio') {
          return;
        }

        const input = option.querySelector('.upsell-bundles__input');
        if (input) {
          input.checked = true;
          this.handleSelection(input);
        }
      });
    });

    // Add change handlers for radio inputs
    inputs.forEach((input) => {
      input.addEventListener('change', () => {
        if (input.checked) {
          this.handleSelection(input);
        }
      });
    });

    // Override form submission
    this.overrideFormSubmission();
  }

  handleSelection(input) {
    const option = input.closest('.upsell-bundles__option');
    if (!option) return;

    // Remove active class from all options
    this.querySelectorAll('.upsell-bundles__option').forEach((opt) => {
      opt.classList.remove('upsell-bundles__option--active');
    });

    // Add active class to selected option
    option.classList.add('upsell-bundles__option--active');

    // Store selected quantity, identifier, and prices
    this.selectedQuantity = parseInt(option.dataset.quantity, 10) || 1;
    this.selectedUpsellId = option.dataset.identifier;
    this.selectedPrice = option.dataset.price ? parseFloat(option.dataset.price) : null;
    this.selectedComparePrice = option.dataset.comparePrice ? parseFloat(option.dataset.comparePrice) : null;

    // Update quantity immediately when selection changes
    this.updateQuantity();

    // Update button price (with a small delay to ensure button is ready)
    setTimeout(() => {
      this.updateButtonPrice();
    }, 0);

    // Show/hide USPs
    this.updateUSPs(option);
  }

  updateUSPs(selectedOption) {
    // Hide all USPs
    this.querySelectorAll('.upsell-bundles__usps').forEach((usps) => {
      usps.style.display = 'none';
    });

    // Show USPs for selected option only if they exist
    const selectedUSPs = selectedOption.querySelector('.upsell-bundles__usps');
    if (selectedUSPs && selectedUSPs.children.length > 0) {
      selectedUSPs.style.display = 'flex';
    }
  }

  overrideFormSubmission() {
    const form = this.getProductForm();
    if (!form) return;

    // Update quantity whenever selection changes
    this.updateQuantity();

    // Listen for form submission - update quantity right before submit
    form.addEventListener('submit', (e) => {
      if (this.isSubmitting) {
        e.preventDefault();
        return false;
      }

      // Update quantity before submission
      this.updateQuantity();

      // Prevent double submission
      this.isSubmitting = true;
      
      // Re-enable after a delay (in case of error)
      setTimeout(() => {
        this.isSubmitting = false;
      }, 2000);
    });
  }

  updateButtonPrice(retryCount = 0) {
    // Prevent infinite loops
    if (this.isUpdatingButton) {
      return;
    }

    if (!this.selectedPrice) {
      this.updateButtonToVariantPrice();
      return;
    }

    const form = this.getProductForm();
    if (!form) {
      // Retry if form isn't ready yet (max 10 retries = 500ms)
      if (retryCount < 10) {
        setTimeout(() => this.updateButtonPrice(retryCount + 1), 50);
      }
      return;
    }

    const button = form.querySelector('.add-to-cart-button');
    if (!button) {
      // Retry if button isn't ready yet
      if (retryCount < 10) {
        setTimeout(() => this.updateButtonPrice(retryCount + 1), 50);
      }
      return;
    }

    const buttonContent = button.querySelector('.button__content');
    if (!buttonContent) {
      // Retry if button content isn't ready yet
      if (retryCount < 10) {
        setTimeout(() => this.updateButtonPrice(retryCount + 1), 50);
      }
      return;
    }

    // Set flag to prevent observer from triggering
    this.isUpdatingButton = true;

    // Temporarily disconnect observer to prevent loop
    if (this.buttonObserver) {
      this.buttonObserver.disconnect();
    }

    // Get the base button text
    // Try to preserve the original base text, but if we can't find it, use defaults
    let baseText = 'Add to cart';
    const currentText = buttonContent.textContent || buttonContent.innerText || '';
    
    // Check if button already has price (from buy-buttons script)
    if (currentText.includes(' - ')) {
      baseText = currentText.split(' - ')[0].trim();
    } else if (currentText.includes('Sold out') || currentText.includes('Unavailable')) {
      // Don't update if sold out/unavailable
      this.isUpdatingButton = false;
      // Reconnect observer
      if (this.buttonObserver && this.selectedPrice) {
        this.setupButtonObserver();
      }
      return;
    } else {
      // If button doesn't have price yet, try to get base text from translation
      // This happens on initial load before buy-buttons script runs
      baseText = 'Add to cart';
    }
    
    // Format price (price is already in cents from Shopify)
    const currencyCode = window.themeVariables?.settings?.currency_code_enabled === true;
    const currency = window.Shopify?.currency?.active || window.Shopify?.shop?.currency || 'ZAR';
    const price = this.formatMoney(this.selectedPrice, currencyCode, currency);
    
    // Structure for responsive layout: text and price in separate spans
    let priceHtml = '';
    if (this.selectedComparePrice && this.selectedComparePrice > this.selectedPrice) {
      const compareAtPrice = this.formatMoney(this.selectedComparePrice, currencyCode, currency);
      priceHtml = '<span class="upsell-bundles__button-price"><span class="line-through text-sm">' + compareAtPrice + '</span> ' + price + '</span>';
    } else {
      priceHtml = '<span class="upsell-bundles__button-price">' + price + '</span>';
    }

    // Create structure: text on one line, price on another (mobile) or inline (desktop)
    const buttonText = baseText + ' <span class="upsell-bundles__button-price-wrapper"><span class="upsell-bundles__button-dash">- </span>' + priceHtml + '</span>';

    buttonContent.innerHTML = buttonText;

    // Reset flag and reconnect observer after a small delay
    setTimeout(() => {
      this.isUpdatingButton = false;
      // Reconnect observer if we have a selected price
      if (this.selectedPrice) {
        this.setupButtonObserver();
      }
    }, 100);
  }

  setupButtonObserver() {
    // Disconnect existing observer if it exists
    if (this.buttonObserver) {
      this.buttonObserver.disconnect();
      this.buttonObserver = null;
    }

    // Watch for changes to the button content and override with upsell price
    const form = this.getProductForm();
    if (!form) return;

    const button = form.querySelector('.add-to-cart-button');
    if (!button) return;

    const buttonContent = button.querySelector('.button__content');
    if (!buttonContent) return;

    // Create observer to watch for content changes
    const observer = new MutationObserver(() => {
      // Only override if we have a selected upsell price and we're not already updating
      if (this.selectedPrice && !this.isUpdatingButton) {
        // Small delay to let the other script finish, then override
        setTimeout(() => {
          // Double-check we're still not updating (race condition protection)
          if (!this.isUpdatingButton && this.selectedPrice) {
            this.updateButtonPrice();
          }
        }, 50);
      }
    });

    // Observe changes to button content
    observer.observe(buttonContent, {
      childList: true,
      subtree: true,
      characterData: true
    });

    // Store observer so we can disconnect later if needed
    this.buttonObserver = observer;
  }

  updateButtonToVariantPrice() {
    // Reset button to show variant price (let the existing script handle it)
    // This is called when no upsell is selected or when deselecting
    const form = this.getProductForm();
    if (!form) return;

    // Disconnect observer if it exists
    if (this.buttonObserver) {
      this.buttonObserver.disconnect();
      this.buttonObserver = null;
    }

    // Trigger variant change event to update button price
    const variantInput = form.querySelector('input[name="id"]');
    if (variantInput) {
      const variantId = variantInput.value;
      // The existing buy-buttons script will handle the price update
      form.dispatchEvent(new CustomEvent('variant:change', {
        detail: { variant: { id: variantId } }
      }));
    }
  }

  formatMoney(cents, useCurrencyCode, currency) {
    const amount = cents / 100;
    
    if (useCurrencyCode) {
      try {
        return new Intl.NumberFormat('en-ZA', {
          style: 'currency',
          currency: currency || 'ZAR'
        }).format(amount);
      } catch (e) {
        return 'R' + amount.toFixed(2);
      }
    }
    
    return 'R' + amount.toFixed(2);
  }

  updateQuantity() {
    if (!this.selectedQuantity || this.selectedQuantity <= 0) return;

    const form = this.getProductForm();
    if (!form) return;

    // Find quantity selector component first (custom element)
    const quantitySelector = form.querySelector('quantity-selector');
    if (quantitySelector) {
      // Find the quantity-input custom element
      const quantityInputElement = quantitySelector.querySelector('quantity-input');
      if (quantityInputElement) {
        // Get the actual input inside quantity-input
        const input = quantityInputElement.querySelector('input[name="quantity"]');
        if (input) {
          input.value = this.selectedQuantity;
          // Trigger events to update the custom element
          input.dispatchEvent(new Event('input', { bubbles: true }));
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      } else {
        // Fallback: direct input in quantity-selector
        const input = quantitySelector.querySelector('input[name="quantity"]');
        if (input) {
          input.value = this.selectedQuantity;
          input.dispatchEvent(new Event('input', { bubbles: true }));
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    } else {
      // No quantity selector, look for direct quantity input
      const quantityInput = form.querySelector('input[name="quantity"]');
      if (quantityInput) {
        quantityInput.value = this.selectedQuantity;
        quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        // Create hidden quantity input if it doesn't exist
        let hiddenInput = form.querySelector('input[name="quantity"][type="hidden"]');
        if (!hiddenInput) {
          hiddenInput = document.createElement('input');
          hiddenInput.type = 'hidden';
          hiddenInput.name = 'quantity';
          form.appendChild(hiddenInput);
        }
        hiddenInput.value = this.selectedQuantity;
      }
    }
  }
}

if (!customElements.get('upsell-bundles')) {
  customElements.define('upsell-bundles', UpsellBundles);
}

